/**
 * Responsibility: Derive lightweight atlas node palettes from room images.
 * Scope: Best-effort UI palette extraction only; rendering consumes the result.
 */

export interface AtlasNodePalette {
  start: string
  end: string
  stroke: string
  glow: string
}

const paletteCache = new Map<string, Promise<AtlasNodePalette | null>>()

function clampChannel(value: number) {
  return Math.max(0, Math.min(255, Math.round(value)))
}

function parseChannels(rgb: string): [number, number, number] {
  const match = String(rgb).match(/\d+(?:\.\d+)?/g) ?? []
  const [r, g, b] = match.slice(0, 3).map((value) => Number.parseFloat(value) || 0)
  return [r, g, b]
}

function rgbaString(channels: [number, number, number], alpha = 1) {
  const [r, g, b] = channels.map(clampChannel) as [number, number, number]
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function mix(a: [number, number, number], b: [number, number, number], ratio = 0.5): [number, number, number] {
  const weight = Math.max(0, Math.min(1, ratio))
  return [
    (a[0] * (1 - weight)) + (b[0] * weight),
    (a[1] * (1 - weight)) + (b[1] * weight),
    (a[2] * (1 - weight)) + (b[2] * weight),
  ]
}

function brighten(channels: [number, number, number], amount: number): [number, number, number] {
  return [
    channels[0] + ((255 - channels[0]) * amount),
    channels[1] + ((255 - channels[1]) * amount),
    channels[2] + ((255 - channels[2]) * amount),
  ]
}

function deepen(channels: [number, number, number], amount: number): [number, number, number] {
  return [
    channels[0] * (1 - amount),
    channels[1] * (1 - amount),
    channels[2] * (1 - amount),
  ]
}

function averageRegion(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number): [number, number, number] {
  const data = ctx.getImageData(x, y, width, height).data
  let r = 0
  let g = 0
  let b = 0
  let count = 0
  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3] / 255
    if (alpha <= 0.01) continue
    r += data[i] * alpha
    g += data[i + 1] * alpha
    b += data[i + 2] * alpha
    count += alpha
  }
  if (count <= 0) return [32, 48, 72]
  return [r / count, g / count, b / count]
}

async function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.decoding = 'async'
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}

async function buildPalette(url: string): Promise<AtlasNodePalette | null> {
  if (typeof document === 'undefined') return null
  try {
    const img = await loadImage(url)
    const canvas = document.createElement('canvas')
    canvas.width = 24
    canvas.height = 24
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return null
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

    const top = averageRegion(ctx, 0, 0, canvas.width, 10)
    const bottom = averageRegion(ctx, 0, 14, canvas.width, 10)
    const center = averageRegion(ctx, 6, 6, 12, 12)

    const brightTop = brighten(mix(top, center, 0.35), 0.24)
    const deepBottom = deepen(mix(bottom, center, 0.3), 0.34)
    const stroke = brighten(mix(brightTop, center, 0.45), 0.1)
    const glow = mix(brightTop, stroke, 0.5)

    return {
      start: rgbaString(brightTop, 0.96),
      end: rgbaString(deepBottom, 0.98),
      stroke: rgbaString(stroke, 0.94),
      glow: rgbaString(glow, 0.28),
    }
  } catch {
    return null
  }
}

export function fallbackAtlasNodePalette(kind: 'room' | 'passage', isCurrent = false): AtlasNodePalette {
  if (isCurrent) {
    return {
      start: 'rgba(255, 244, 182, 0.98)',
      end: 'rgba(143, 80, 8, 0.98)',
      stroke: 'rgba(252, 219, 122, 0.96)',
      glow: 'rgba(252, 219, 122, 0.3)',
    }
  }
  if (kind === 'passage') {
    return {
      start: 'rgba(68, 103, 148, 0.92)',
      end: 'rgba(8, 15, 28, 0.98)',
      stroke: 'rgba(166, 198, 244, 0.86)',
      glow: 'rgba(112, 161, 238, 0.24)',
    }
  }
  return {
    start: 'rgba(132, 168, 224, 0.94)',
    end: 'rgba(7, 13, 22, 0.98)',
    stroke: 'rgba(166, 198, 244, 0.88)',
    glow: 'rgba(112, 161, 238, 0.24)',
  }
}

export function atlasPaletteFromCssGradient(value: string, kind: 'room' | 'passage', isCurrent = false): AtlasNodePalette {
  const matches = String(value).match(/rgba?\([^)]+\)/g) ?? []
  if (matches.length >= 2) {
    const start = parseChannels(matches[0] || '')
    const end = parseChannels(matches[matches.length - 1] || '')
    const stroke = brighten(mix(start, end, 0.35), 0.18)
    const glow = mix(start, stroke, 0.42)
    return {
      start: rgbaString(brighten(start, kind === 'passage' ? 0.08 : 0.18), 0.96),
      end: rgbaString(deepen(end, isCurrent ? 0.14 : 0.24), 0.98),
      stroke: rgbaString(stroke, 0.94),
      glow: rgbaString(glow, 0.28),
    }
  }
  return fallbackAtlasNodePalette(kind, isCurrent)
}

export function deriveAtlasNodePalette(url: string): Promise<AtlasNodePalette | null> {
  const key = String(url || '').trim()
  if (!key) return Promise.resolve(null)
  if (!paletteCache.has(key)) paletteCache.set(key, buildPalette(key))
  return paletteCache.get(key) as Promise<AtlasNodePalette | null>
}
